import React, { useState, useRef, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BrandContext } from '../context/BrandContext';
import StructuredCardRenderer from '../components/ai/StructuredCardRenderer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { streamChat, streamAddTable, streamAppendDelta, streamReset, streamFinalize, splitSourceLine } from '../utils/sseChat';
import GlowyDataTable from '../components/GlowyDataTable';

// GlowyAI sunum linki (/glowyai/sunum/<id>) sohbette kart olarak görünür ve yeni sekmede açılır.
// Diğer linkler olduğu gibi kalır.
function MarkdownLink({ href, children }) {
  if (typeof href === 'string' && href.startsWith('/glowyai/sunum/')) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
        className="not-prose my-2 flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 no-underline shadow-sm hover:border-violet-300 hover:shadow">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M12 16v4M8 20h8" /></svg>
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-gray-900">{children}</span>
          <span className="block text-xs text-gray-500">Sunumu aç · PDF olarak indirilebilir</span>
        </span>
      </a>
    );
  }
  return <a href={href}>{children}</a>;
}

export default function Glowyaigamini() {
  const { selectedBrand } = useContext(BrandContext) || {};
  const [sessions, setSessions] = useState([]); // Holds the list of conversations
  const [activeSessionId, setActiveSessionId] = useState(null); // The ID of the currently active conversation
  const [messages, setMessages] = useState([]);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [introAnimating, setIntroAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const lastMessageCountRef = useRef(0);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [typing, setTyping] = useState(false);
  const [streamStatus, setStreamStatus] = useState('');
  const [streamHasText, setStreamHasText] = useState(false);
  const streamAbortRef = useRef(null);
  const streamBufRef = useRef('');
  const streamRafRef = useRef(null);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const suggestions = [
    'Last 7-day summary',
    'Monthly breakdown',
    'Google vs Meta ROAS',
    'Top channel last 60 days'
  ];

  const sessionsAbortRef = useRef(null);
  const sessionsReqIdRef = useRef(0);

  const [userId] = useState(() => {
    const existing = localStorage.getItem('glowyAiUserId');
    if (existing) return existing;
    const id = uuidv4();
    localStorage.setItem('glowyAiUserId', id);
    return id;
  });

  // Track whether the user has manually scrolled up. If so, suppress auto-scroll.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      // Within 80px of bottom = considered "at bottom"
      const atBottom = distanceFromBottom < 80;
      userScrolledUpRef.current = !atBottom;
      setShowJumpToBottom(!atBottom);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll only when:
  //   (a) a NEW message just arrived (not when an existing one streams more text), OR
  //   (b) the user is already at/near the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isNewMessage = messages.length > lastMessageCountRef.current;
    lastMessageCountRef.current = messages.length;
    if (isNewMessage || !userScrolledUpRef.current) {
      el.scrollTop = el.scrollHeight;
      userScrolledUpRef.current = false;
      setShowJumpToBottom(false);
    }
  }, [messages, sending, typing]);

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    userScrolledUpRef.current = false;
    setShowJumpToBottom(false);
  };

  useEffect(() => {
    setSessions([]);
    const existingBrandId = (() => {
      try {
        return localStorage.getItem('currentBrandId')
          || (selectedBrand && selectedBrand.id)
          || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; } })();
      } catch {
        return localStorage.getItem('currentBrandId') || (selectedBrand && selectedBrand.id) || null;
      }
    })();
    // Invalidate any in-flight before starting a new one
    sessionsReqIdRef.current += 1;
    // Swallow any stray rejections (e.g., StrictMode double invoke)
    loadSessions().catch(() => {});
    // Cleanup: abort on unmount or dependency change
    return () => {
      sessionsReqIdRef.current += 1; // invalidate pending responses
    };
  }, [selectedBrand]);

  // Sayfadan cikinca devam eden streaming istegini iptal et
  useEffect(() => () => {
    if (streamAbortRef.current) { try { streamAbortRef.current.abort(); } catch (_) { /* ignore */ } }
    if (streamRafRef.current) { try { cancelAnimationFrame(streamRafRef.current); } catch (_) { /* ignore */ } }
  }, []);

  // Swallow AbortError unhandled promise rejections to avoid noisy dev overlay
  useEffect(() => {
    const onUnhandledRejection = (event) => {
      try {
        const reason = event?.reason;
        const name = reason?.name || '';
        const msg = (reason?.message || String(reason || ''));
        const isAbort = name === 'AbortError' || /aborted/i.test(msg) || /signal is aborted/i.test(msg) || reason?.code === 20;
        if (isAbort) {
          event.preventDefault();
        }
      } catch (_e) {
        // ignore
      }
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', onUnhandledRejection);
  }, []);

  const getApiBaseUrl = () => {
    const isPlatformHost = (typeof window !== 'undefined') && /(^|\.)platform\.glowytics\.com$/i.test(window.location.hostname);
    return isPlatformHost
      ? 'https://api.glowytics.com'
      : (process.env.NODE_ENV === 'development' ? '' : 'https://api.glowytics.com');
  };

  const loadSessions = async () => {
    const brandId = localStorage.getItem('currentBrandId') 
      || (selectedBrand && selectedBrand.id) 
      || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; } })();
    const authToken = localStorage.getItem('token');
    console.log('loadSessions:init', { brandId, tokenExists: !!authToken, tokenLen: authToken ? authToken.length : 0 });
    if (!brandId) {
      console.warn('loadSessions: missing brandId');
      setSessions([]);
      return;
    }

    try {
      // mark new request
      const reqId = ++sessionsReqIdRef.current;
      const url = `${getApiBaseUrl()}/api/ai/conversations`;
      console.log('Loading conversations...', { url, brandId });
      const headers = { 'X-Brand-ID': brandId };
      if (authToken && authToken !== 'undefined' && authToken !== 'null') headers['Authorization'] = `Bearer ${authToken}`;
      const resp = await fetch(url, {
        headers,
        credentials: 'include'
      });

      console.log('Conversations response status:', resp.status);
      if (resp.status === 204) {
        if (reqId === sessionsReqIdRef.current) setSessions([]);
        return;
      }
      if (resp.ok) {
        const data = await resp.json();
        console.log('Conversations payload:', data);
        if (reqId === sessionsReqIdRef.current) setSessions(data.conversations || data.sessions || []);
      } else {
        const txt = await resp.text();
        console.warn('Failed to load conversations:', resp.status, txt);
        if (reqId === sessionsReqIdRef.current) setSessions([]);
      }
    } catch (e) {
      const msg = (e && (e.message || e.toString())) || '';
      const isAbort = e?.name === 'AbortError'
        || e?.code === 20
        || /aborted/i.test(msg)
        || /The user aborted a request/i.test(msg)
        || /signal is aborted/i.test(msg);
      if (isAbort) {
        // Silently ignore aborts to avoid dev overlay noise
        console.debug('Conversations request aborted');
        return;
      }
      console.warn('Failed to load conversations:', e);
      setSessions([]);
    }
  };

  const loadMessages = async (conversationId) => {
    const brandId = localStorage.getItem('currentBrandId') || (selectedBrand && selectedBrand.id);
    const authToken = localStorage.getItem('token');
    if (!brandId || !conversationId) return;

    try {
      let url = `${getApiBaseUrl()}/api/ai/conversations/${conversationId}`;
      console.log('Loading messages (primary)...', { url, conversationId });
      const headers = { 'X-Brand-ID': brandId };
      if (authToken && authToken !== 'undefined' && authToken !== 'null') headers['Authorization'] = `Bearer ${authToken}`;
      let resp = await fetch(url, {
        headers,
        credentials: 'include'
      });

      if (resp.ok) {
        const data = await resp.json();
        console.log('Messages payload (primary):', data);
        const formatted = (data.messages || []).map((m) => ({
          ...m,
          role: (m.sender_role ? m.sender_role.toLowerCase() : m.role || 'assistant')
        }));
        setMessages(formatted);
        return;
      }
      console.warn('Failed to load messages:', resp.status);
    } catch (e) {
      console.warn('Failed to load messages:', e);
    }
  };

  const stopStreaming = () => {
    if (streamAbortRef.current) { try { streamAbortRef.current.abort(); } catch (_) { /* ignore */ } }
  };

  const createNewSession = () => {
    stopStreaming();
    setActiveSessionId(null);
    setMessages([]);
  };
  
  const selectSession = async (id) => {
    stopStreaming();
    setActiveSessionId(id);
    await loadMessages(id);
  };

  const handleDeleteSession = async (id) => {
    try {
      const brandId = localStorage.getItem('currentBrandId') 
        || (selectedBrand && selectedBrand.id) 
        || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; } })();
      if (!brandId || !id) return;
      setDeletingSessionId(id);
      const authToken = localStorage.getItem('token');
      const headers = { 'X-Brand-ID': brandId };
      if (authToken && authToken !== 'undefined' && authToken !== 'null') headers['Authorization'] = `Bearer ${authToken}`;
      const resp = await fetch(`${getApiBaseUrl()}/api/ai/conversations/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        console.warn('Delete conversation failed', resp.status, txt);
        return;
      }
      if (activeSessionId === id) {
        setActiveSessionId(null);
        setMessages([]);
      }
      await loadSessions();
    } catch (e) {
      console.warn('Delete conversation error:', e);
    } finally {
      setDeletingSessionId(null);
    }
  };

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? inputValue).trim();
    if (!text) return;

    // If intro hero is visible, animate it out before first send
    const introVisibleNow = (!introDismissed && !activeSessionId && messages.length === 0);
    if (introVisibleNow) {
      setIntroAnimating(true);
      setTimeout(() => {
        setIntroDismissed(true);
        setIntroAnimating(false);
      }, 650);
    }

    const brandId = localStorage.getItem('currentBrandId')
      || (selectedBrand && selectedBrand.id)
      || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; }})();
    const brandName = (() => { try {
      return (selectedBrand && selectedBrand.name)
        || (JSON.parse(localStorage.getItem('selectedBrand') || '{}').name)
        || localStorage.getItem('currentBrandName')
        || null;
    } catch { return (selectedBrand && selectedBrand.name) || null; } })();
    
    if (!brandId) {
      setMessages((prev) => [
        ...prev,
        { role: 'system', content: 'No brand selected. Please choose a brand first.' }
      ]);
      return;
    }

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setSending(true);

    try {
      const authToken = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'X-Brand-ID': brandId
      };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const options = { narrative: true };

      const payload = {
        conversationId: activeSessionId,
        message: text,
        options,
        userId,
        brandName
      };

      const baseUrl = getApiBaseUrl();
      const streamEnabled = (() => { try { return localStorage.getItem('glowyai_stream') !== '0'; } catch { return true; } })();
      const streamId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      let streamBubbleCreated = false;
      let streamTableCount = 0;
      let data = null;

      const flushDelta = () => {
        streamRafRef.current = null;
        const chunk = streamBufRef.current;
        if (!chunk) return;
        streamBufRef.current = '';
        if (!streamBubbleCreated) {
          streamBubbleCreated = true;
          setStreamHasText(true);
        }
        setMessages((prev) => streamAppendDelta(prev, streamId, chunk));
      };

      if (streamEnabled && typeof ReadableStream !== 'undefined' && typeof TextDecoder !== 'undefined') {
        const controller = new AbortController();
        streamAbortRef.current = controller;
        setStreamStatus('');
        setStreamHasText(false);
        try {
          data = await streamChat({
            url: `${baseUrl}/api/ai/gemini-chat/v1/respond-stream`,
            headers,
            body: payload,
            signal: controller.signal,
            onMeta: (m) => { if (m && m.conversationId && !activeSessionId) setActiveSessionId(m.conversationId); },
            onStatus: (msg) => { if (msg) setStreamStatus(msg); },
            onDelta: (t) => {
              streamBufRef.current += t;
              if (!streamRafRef.current) streamRafRef.current = requestAnimationFrame(flushDelta);
            },
            onTable: (tbl) => {
              streamBubbleCreated = true;
              streamTableCount += 1;
              setStreamHasText(true);
              setMessages((prev) => streamAddTable(prev, streamId, tbl));
            },
            onReset: () => {
              streamBufRef.current = '';
              if (streamRafRef.current) { cancelAnimationFrame(streamRafRef.current); streamRafRef.current = null; }
              if (streamBubbleCreated) {
                // Tablo varsa balon kalir, yalniz yorum taslagi temizlenir
                if (streamTableCount === 0) {
                  streamBubbleCreated = false;
                  setStreamHasText(false);
                }
                setMessages((prev) => streamReset(prev, streamId));
              }
            }
          });
        } catch (se) {
          if (streamRafRef.current) { cancelAnimationFrame(streamRafRef.current); streamRafRef.current = null; }
          streamBufRef.current = '';
          if (se.aborted) {
            // Kullanici durdurdu / sayfa degisti: taslak kalsin, isaretle
            setMessages((prev) => prev.map((m) => (m._sid === streamId ? { ...m, _streaming: false } : m)));
            return;
          }
          if (se.streamFallbackOk && !streamBubbleCreated) {
            console.warn('Streaming unavailable, falling back to /respond:', se.message);
            data = null; // asagida eski uca dus
          } else {
            // Delta geldikten sonra kopma: otomatik tekrar YOK, kullanici tekrar denesin
            setMessages((prev) => [
              ...prev.filter((m) => m._sid !== streamId || (m.content && m.content.length > 0) || (Array.isArray(m.tables) && m.tables.length > 0)).map((m) => (m._sid === streamId ? { ...m, _streaming: false } : m)),
              { role: 'system', content: `Error: ${se.message}`, retryText: text }
            ]);
            return;
          }
        } finally {
          streamAbortRef.current = null;
        }
        // done geldi: bekleyen rAF parcasini at. Nihai mesaj tum metni zaten iceriyor;
        // birakilirsa balon _sid'siz kaldigi icin parca ("ım?") yeni balon acar.
        if (streamRafRef.current) { cancelAnimationFrame(streamRafRef.current); streamRafRef.current = null; }
        streamBufRef.current = '';
      }

      if (!data) {
        setStreamStatus('');
        const resp = await fetch(`${baseUrl}/api/ai/gemini-chat/v1/respond`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(payload)
        });

        if (!resp.ok) {
          const errText = await resp.text();
          throw new Error(errText || `HTTP ${resp.status}`);
        }

        data = await resp.json();
      }

      if (data.conversationId && !activeSessionId) {
        setActiveSessionId(data.conversationId);
      }

      // --- HYBRID RENDERING: Show Narrative Text + Structured Card if available ---
      const doneTables = Array.isArray(data?.tables) ? data.tables.filter((t) => t && Array.isArray(t.columns) && Array.isArray(t.rows)) : [];
      const hasTables = doneTables.length > 0;
      const fullAssistantText = data?.assistantMessage || 'No response.';
      // Tablolu cevapta isaretciler (cards/KPI) yorum metninde olur; tablo markdown'i bilesen olarak cizilir
      const assistantMessage = hasTables ? (typeof data.commentary === 'string' ? data.commentary : '') : fullAssistantText;
      let extras = {};

      console.log('Gemini Data Received:', data);

      // 1. KPI Card
      if (data?.structured?.kpiCard) {
        console.log('KPI Card detected');
        extras.kpiCard = data.structured.kpiCard;
      } 
      
      if (data?.structured?.top10Card) {
        console.log('Top 10 Card detected');
        extras.top10Card = data.structured.top10Card;
      }

      // 2. Campaign Inventory Performance
      if (data?.structured?.campaign_inventory_perf) {
        console.log('Campaign Perf Card detected');
        try {
          const perf = data.structured.campaign_inventory_perf;
          const makeRows = (arr) => (Array.isArray(arr) ? [...arr] : [])
            .sort((a,b)=> Number(b.cost||0) - Number(a.cost||0))
            .slice(0, 12);
          
          extras.campaignPerfCard = {
            title: 'Active campaigns — Performance Snapshot',
            period: perf.period || {},
            google: makeRows(perf.google),
            meta: makeRows(perf.meta),
            totals: perf.totals || {},
            groups: perf.groups || {},
            roas_trend: perf.roas_trend || null
          };
        } catch (err) { console.error('Error parsing campaignPerfCard', err); }
      }

      // New generic cards
      if (data?.cards && Array.isArray(data.cards)) {
        extras.cards = data.cards;
      }

      // 3. Category Top Recent
      if (data?.structured?.category_top_recent && Array.isArray(data.structured.category_top_recent.rows)) {
        console.log('Category Card detected');
        try {
          const s = data.structured.category_top_recent;
          extras.categoryCard = {
            title: 'Top categories (Last 30 days)',
            period: s.period || {},
            rows: s.rows.map(r => ({ 
                category: r.category, 
                revenue: Math.round(Number(r.revenue||0)), 
                purchases: Number(r.purchases||0), 
                views: Number(r.views||0),
                add_to_cart: Number(r.add_to_cart||0)
            }))
          };
        } catch (err) { console.error('Error parsing categoryCard', err); }
      }

      // If narrative text contains a hidden marker (from history/DB), extract it
      let cleanText = assistantMessage;

      // Unwrap accidental JSON-wrapped responses: when Gemini returns {"answer":"...","table":{...}}
      // as a raw string instead of plain markdown, parse it and extract the human-readable parts.
      if (typeof assistantMessage === 'string') {
        const trimmed = assistantMessage.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}') && trimmed.includes('"answer"')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed.answer === 'string') {
              cleanText = parsed.answer;
              // If parsed.table exists, render it as a markdown table appended to the answer
              if (parsed.table && Array.isArray(parsed.table.columns) && Array.isArray(parsed.table.rows)) {
                const cols = parsed.table.columns;
                const header = `| ${cols.join(' | ')} |`;
                const sep = `| ${cols.map(() => '---').join(' | ')} |`;
                const body = parsed.table.rows.slice(0, 25).map(r => `| ${r.join(' | ')} |`).join('\n');
                cleanText = `${cleanText}\n\n${header}\n${sep}\n${body}`;
              }
            }
          } catch (_) { /* leave cleanText as-is */ }
        }
      }

      if (typeof assistantMessage === 'string' && assistantMessage.includes('__CARDS_V1__')) {
        const parts = assistantMessage.split('__CARDS_V1__');
        cleanText = parts[0].trim();
        try { extras.cards = JSON.parse(parts[1]); } catch (e) {}
      }
      if (typeof assistantMessage === 'string') {
          if (assistantMessage.includes('__KPI_CARD_V1__')) {
              const parts = assistantMessage.split('__KPI_CARD_V1__');
              cleanText = parts[0].trim();
              try { extras.kpiCard = JSON.parse(parts[1]); } catch (e) {}
          } else if (assistantMessage.includes('__KPI_CARD_CAMPAIGN_V1__')) {
              const parts = assistantMessage.split('__KPI_CARD_CAMPAIGN_V1__');
              cleanText = parts[0].trim();
              try { extras.campaignPerfCard = JSON.parse(parts[1]); } catch (e) {}
          } else if (assistantMessage.includes('__KPI_CARD_TOP10_PRODUCTS_V1__')) {
              const parts = assistantMessage.split('__KPI_CARD_TOP10_PRODUCTS_V1__');
              cleanText = parts[0].trim();
              try { extras.top10Card = JSON.parse(parts[1]); } catch (e) {}
          } else if (assistantMessage.includes('__KPI_CARD_CATEGORY_V1__')) {
              const parts = assistantMessage.split('__KPI_CARD_CATEGORY_V1__');
              cleanText = parts[0].trim();
              try { extras.categoryCard = JSON.parse(parts[1]); } catch (e) {}
          }
      }

      // Start typing the narrative, and the card will appear alongside it
      if (hasTables) {
        // Tablolar bilesen, yorum markdown; birlesik metin (fullText) kopyalama/mail icin saklanir
        const finalMsg = { role: 'assistant', content: cleanText, tables: doneTables, fullText: fullAssistantText, ...extras };
        setMessages((prev) => streamFinalize(prev, streamId, finalMsg));
      } else if (streamBubbleCreated) {
        // Akis balonu nihai icerikle degissin (daktilo animasyonu yok)
        setMessages((prev) => prev.map((m) => (m._sid === streamId
          ? { role: 'assistant', content: cleanText, ...extras }
          : m)));
      } else {
        await appendAssistantTypewriter(cleanText, extras);
      }
      
      // Refresh session list to show new message preview and timestamp
      await loadSessions();

    } catch (e) {
      setMessages((prev) => [...prev, { role: 'system', content: `Error: ${e.message}` }]);
    } finally {
      setSending(false);
      setStreamStatus('');
      setStreamHasText(false);
    }
  };

  const appendAssistantTypewriter = (fullText, extras = {}) => new Promise((resolve) => {
    const TYPE_INTERVAL = 16;
    const CHARS_PER_TICK = 2;
    setTyping(true);
    let targetIndex = -1;
    setMessages((prev) => {
      targetIndex = prev.length;
      return [...prev, { role: 'assistant', content: '', ...extras }];
    });
    let i = 0;
    const interval = setInterval(() => {
      i = Math.min(fullText.length, i + CHARS_PER_TICK);
      const slice = fullText.slice(0, i);
      setMessages((prev) => prev.map((m, idx) => (idx === targetIndex ? { ...m, content: slice } : m)));
      if (i >= fullText.length) {
        clearInterval(interval);
        setTyping(false);
        resolve();
      }
    }, TYPE_INTERVAL);
  });

  const handleQuick = (mode) => {
    // Intent mode removed - not used
    if (mode === 'weekly') sendMessage('Show weekly performance summary');
    else if (mode === 'monthly') sendMessage('Show monthly performance summary');
    else sendMessage('Show daily performance');
  };

  return (
    <div className="w-full h-[calc(100vh-80px)] p-4 flex flex-col gap-4 min-h-0">
      {/* Removed standalone header card; branding moved inside chat card */}
      {/* Layout: main chat + right sidebar */}
      <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
        {/* Chat area (messages + input in one card) */}
        <div className="col-span-12 lg:col-span-10 h-full min-h-0">
          <div className="h-full min-h-0 bg-white rounded-2xl shadow-md border border-gray-300 flex flex-col">
            <div className="p-4 pb-3 border-b border-gray-300 bg-gradient-to-r from-white to-[#f8fafc] rounded-t-2xl relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none opacity-60" style={{backgroundImage: 'radial-gradient(40% 60% at 10% -10%, rgba(124,58,237,0.10), transparent 60%), radial-gradient(50% 70% at 90% -10%, rgba(37,99,235,0.08), transparent 60%)'}} />
              <div className="relative z-10 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full overflow-hidden ring-2 ring-violet-200 shadow-sm bg-white">
                  <img src="/GlowyAIv3.png" alt="GlowyAI" className="h-full w-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold text-gray-900 tracking-[-0.01em]">GlowyAI</h1>
                    <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 bg-white text-[10px] text-gray-600">
                      <img src="/ai.svg" alt="Claude" className="h-3.5 w-3.5 object-contain" />
                      Powered by Anthropic Claude
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">Your AI marketing co‑pilot</p>
                </div>
                <div className="hidden lg:flex items-center gap-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500"/>Online</span>
                </div>
              </div>
              <div className="relative z-10 mt-3 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => sendMessage(s)} className="px-2.5 py-1 text-xs rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700">
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 relative">
              <div className="space-y-3">
                {messages.map((m, idx) => {
                  const isSystem = m.role === 'system';
                  const isAssistant = !isSystem && m.role !== 'user';
                  const bubbleClass = isSystem
                    ? 'bg-amber-50 text-amber-900 border border-amber-300 shadow-sm'
                    : 'bg-gray-100 text-gray-900 border border-gray-300 shadow-sm';
                  return (
                    <div key={idx} className={`flex ${m.role==='user'?'justify-end':'justify-start'}`}>
                      {isAssistant ? (
                        <div className="flex items-start gap-2 max-w-[80%]">
                          <div className="h-11 w-11 rounded-full overflow-hidden ring-1 ring-violet-200 flex-shrink-0 mt-0.5 bg-white">
                            <img src="/GlowyAIv3.png" alt="GlowyAI" className="h-full w-full object-cover" />
                          </div>
                          <div className={`${bubbleClass} px-3 py-2 rounded-xl text-sm leading-5 shadow-sm`}>
                            {/* 1. Always show the narrative text with Markdown */}
                            {Array.isArray(m.tables) && m.tables.map((t, ti) => (
                              <GlowyDataTable key={t.id || ti} table={t} />
                            ))}
                            {m._streaming && sending && !m.content && Array.isArray(m.tables) && m.tables.length > 0 && (
                              <div className="mb-2 mt-1 inline-flex items-center gap-2 text-xs text-gray-500">
                                <span className="flex gap-0.5">
                                  <span className="h-1.5 w-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                  <span className="h-1.5 w-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                  <span className="h-1.5 w-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                </span>
                                <span>{streamStatus || 'Yorum yazılıyor…'}</span>
                              </div>
                            )}
                            {m.content && (() => {
                              const sp = Array.isArray(m.tables) ? splitSourceLine(m.content) : { body: m.content, source: null };
                              return (
                                <>
                                  {sp.body && <div className="mb-2 ai-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({children}) => <p className="my-1">{children}</p>, ul: ({children}) => <ul className="my-1 ml-4 list-disc">{children}</ul>, ol: ({children}) => <ol className="my-1 ml-4 list-decimal">{children}</ol>, li: ({children}) => <li className="my-0.5">{children}</li>, strong: ({children}) => <strong className="font-semibold">{children}</strong>, a: ({href, children}) => <MarkdownLink href={href}>{children}</MarkdownLink>, h3: ({children}) => <h3 className="text-sm font-bold mt-3 mb-1">{children}</h3>, h4: ({children}) => <h4 className="text-sm font-semibold mt-2 mb-1">{children}</h4>, table: ({children}) => <div className="overflow-x-auto my-2"><table className="min-w-full border-collapse text-xs">{children}</table></div>, thead: ({children}) => <thead className="bg-gray-50">{children}</thead>, th: ({children}) => <th className="border border-gray-200 px-2 py-1.5 font-semibold text-left text-gray-600">{children}</th>, td: ({children}) => <td className="border border-gray-200 px-2 py-1.5">{children}</td> }}>{sp.body}</ReactMarkdown></div>}
                                  {sp.source && <div className="mb-2 text-[11px] text-gray-500">{sp.source}</div>}
                                </>
                              );
                            })()}

                            {/* New generic cards */}
                            {Array.isArray(m.cards) && m.cards.map((card, ci) => (
                              <StructuredCardRenderer key={ci} card={card} />
                            ))}

                            {/* Legacy cards (fallback for old messages only) */}
                            {(!m.cards || m.cards.length === 0) && (<>
                            {/* 2. Show Structured Cards if they exist */}
                            {(m.categoryCard || (typeof m.content === 'string' && m.content.includes('__KPI_CARD_CATEGORY_V1__'))) && (
                              (() => {
                                const c = m.categoryCard || (() => { try { return JSON.parse(m.content.split('__KPI_CARD_CATEGORY_V1__')[1]); } catch { return null; } })();
                                if (!c) return null;
                                return (
                                  <div className="text-[13px] leading-5 mt-3 border-t border-gray-200 pt-3">
                                    <div className="text-xs text-gray-500 mb-2">{c.title || 'Category Performance'}</div>
                                    <div className="overflow-x-auto">
                                      <table className="text-[12px] text-gray-800 min-w-[720px]">
                                        <thead>
                                          <tr className="text-gray-500">
                                            <th className="pr-4 text-left">Category</th>
                                            <th className="pr-4 text-right">Revenue</th>
                                            <th className="pr-4 text-right">Purchases</th>
                                            <th className="pr-4 text-right">Views</th>
                                            <th className="pr-4 text-right">Add to Cart</th>
                                            <th className="pr-0 text-right">CR</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(c.rows || []).map((r, i) => (
                                            <tr key={i} className="border-t border-gray-100">
                                              <td className="pr-4 font-medium">{r.category}</td>
                                              <td className="pr-4 text-right font-semibold">₺{Number(r.revenue||0).toLocaleString()}</td>
                                              <td className="pr-4 text-right">{Number(r.purchases||0).toLocaleString()}</td>
                                              <td className="pr-4 text-right">{Number(r.views||0).toLocaleString()}</td>
                                              <td className="pr-4 text-right">{Number(r.add_to_cart||0).toLocaleString()}</td>
                                              <td className="pr-0 text-right text-purple-600 font-medium">
                                                {Number(r.views||0) > 0 ? ((Number(r.purchases||0)/Number(r.views||0))*100).toFixed(2)+'%' : '0.00%'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()
                            )}

                            {(m.campaignPerfCard || (typeof m.content === 'string' && m.content.includes('__KPI_CARD_CAMPAIGN_V1__'))) && (
                              (() => {
                                const card = m.campaignPerfCard || (() => { try { return JSON.parse(m.content.split('__KPI_CARD_CAMPAIGN_V1__')[1]); } catch { return null; } })();
                                if (!card) return null;
                                return (
                                  <div className="text-[13px] leading-5 mt-3 border-t border-gray-200 pt-3">
                                    <div className="text-xs text-gray-500 mb-2">{card.title}</div>
                                    {[{key:'google', label:'Google Ads', icon:'/google_ads-512.webp'}, {key:'meta', label:'Meta Ads', icon:'/6033716.png'}].map(p => (
                                      Array.isArray(card[p.key]) && card[p.key].length > 0 ? (
                                        <div key={p.key} className="w-full pt-2 mt-2">
                                          <div className="flex items-center gap-2 mb-1"><img src={p.icon} className="h-4 w-4" alt={p.label} /><span className="text-xs font-semibold text-gray-600">{p.label}</span></div>
                                          <div className="overflow-x-auto">
                                            <table className="text-[12px] text-gray-800 min-w-[800px]">
                                              <thead>
                                                <tr className="text-gray-400 border-b border-gray-50">
                                                  <th className="pr-4 text-left pb-1 font-medium">Campaign</th>
                                                  <th className="pr-4 text-right pb-1 font-medium">Budget</th>
                                                  <th className="pr-4 text-right pb-1 font-medium">Cost</th>
                                                  <th className="pr-4 text-right pb-1 font-medium">Revenue</th>
                                                  <th className="pr-4 text-right pb-1 font-medium">ROAS</th>
                                                  <th className="pr-0 text-right pb-1 font-medium">Reco</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {card[p.key].map((r, i) => (
                                                  <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                                                    <td className="pr-4 py-1.5 font-medium truncate max-w-[200px]" title={r.name}>{r.name}</td>
                                                    <td className="pr-4 py-1.5 text-right">₺{Number(r.budget||0).toLocaleString()}</td>
                                                    <td className="pr-4 py-1.5 text-right text-red-600">₺{Number(r.cost||0).toLocaleString()}</td>
                                                    <td className="pr-4 py-1.5 text-right text-green-600">₺{Number(r.revenue||0).toLocaleString()}</td>
                                                    <td className="pr-4 py-1.5 text-right font-bold text-purple-700">{Number(r.roas||0).toFixed(2)}x</td>
                                                    <td className={`pr-0 py-1.5 text-right font-medium capitalize ${r.budget_reco === 'increase' ? 'text-green-500' : r.budget_reco === 'decrease' ? 'text-red-500' : 'text-gray-400'}`}>
                                                      {r.budget_reco || 'Hold'}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      ) : null
                                    ))}
                                  </div>
                                );
                              })()
                            )}

                            {(m.kpiCard || (typeof m.content === 'string' && m.content.includes('__KPI_CARD_V1__'))) && (
                              (() => {
                                let card = m.kpiCard;
                                if (!card && typeof m.content === 'string' && m.content.includes('__KPI_CARD_V1__')) {
                                    try {
                                        const jsonPart = m.content.split('__KPI_CARD_V1__')[1];
                                        card = JSON.parse(jsonPart);
                                    } catch (e) { return null; }
                                }
                                if (!card) return null;

                                const safeRound = (n) => Math.round(Number(n || 0)).toLocaleString();
                                const getCR = (purchases, divider) => {
                                    const p = Number(purchases || 0);
                                    const d = Number(divider || 0);
                                    return d > 0 ? ((p / d) * 100).toFixed(2) + '%' : '0.00%';
                                };

                                return (
                                  <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm w-full mt-3">
                                    <div className="text-[11px] text-gray-400 font-medium mb-3 uppercase tracking-wider">{card.title}</div>
                                    <div className="space-y-5">
                                      <div className="section">
                                        <div className="flex items-center gap-2 mb-2">
                                          <img src="/GA4logoicon.svg" className="h-4 w-4" alt="GA4" />
                                          <span className="text-xs font-semibold text-gray-700">GA4</span>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                          <div><div className="text-[10px] text-gray-400">Sessions</div><div className="text-sm font-bold text-gray-900">{safeRound(card.ga4?.sessions)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">Revenue</div><div className="text-sm font-bold text-green-600">₺{safeRound(card.ga4?.revenue)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">Purchases</div><div className="text-sm font-bold text-gray-900">{safeRound(card.ga4?.purchases)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">CR</div><div className="text-sm font-bold text-purple-600">{getCR(card.ga4?.purchases, card.ga4?.sessions)}</div></div>
                                        </div>
                                      </div>
                                      <div className="h-px bg-gray-100 w-full"></div>
                                      <div className="section">
                                        <div className="flex items-center gap-2 mb-2">
                                          <img src="/google_ads-512.webp" className="h-4 w-4" alt="Google" />
                                          <span className="text-xs font-semibold text-gray-700">Google Ads</span>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                          <div><div className="text-[10px] text-gray-400">Cost</div><div className="text-sm font-bold text-red-600">₺{safeRound(card.google?.cost)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">Revenue</div><div className="text-sm font-bold text-gray-900">₺{safeRound(card.google?.revenue)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">Conversions</div><div className="text-sm font-bold text-gray-900">{safeRound(card.google?.purchases)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">CR</div><div className="text-sm font-bold text-purple-600">{getCR(card.google?.purchases, card.google?.clicks)}</div></div>
                                        </div>
                                      </div>
                                      <div className="h-px bg-gray-100 w-full"></div>
                                      <div className="section">
                                        <div className="flex items-center gap-2 mb-2">
                                          <img src="/6033716.png" className="h-4 w-4" alt="Meta" />
                                          <span className="text-xs font-semibold text-gray-700">Meta Ads</span>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                          <div><div className="text-[10px] text-gray-400">Cost</div><div className="text-sm font-bold text-red-600">₺{safeRound(card.meta?.cost)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">Revenue</div><div className="text-sm font-bold text-gray-900">₺{safeRound(card.meta?.revenue)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">Conversions</div><div className="text-sm font-bold text-gray-900">{safeRound(card.meta?.purchases)}</div></div>
                                          <div><div className="text-[10px] text-gray-400">CR</div><div className="text-sm font-bold text-purple-600">{getCR(card.meta?.purchases, card.meta?.clicks)}</div></div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="mt-4 text-[10px] text-gray-400 text-right font-mono">Period {card.period?.start} → {card.period?.end}</div>
                                  </div>
                                );
                              })()
                            )}

                            {m.top10Card && (
                              (() => {
                                const c = m.top10Card;
                                return (
                                  <div className="text-[13px] leading-5 mt-3 border-t border-gray-200 pt-3">
                                    <div className="text-xs text-gray-500 mb-2">Top 10 Products</div>
                                    <div className="overflow-x-auto">
                                      <table className="text-[12px] text-gray-800 min-w-[540px]">
                                        <thead>
                                          <tr className="text-gray-500">
                                            <th className="pr-4 text-left">Product</th>
                                            <th className="pr-4 text-right">Revenue</th>
                                            <th className="pr-0 text-right">Purchases</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {c.rows.map((r,i)=> (
                                            <tr key={i} className="border-t border-gray-100"><td className="pr-4">{r.name}</td><td className="pr-4 text-right">₺{Number(r.revenue||0).toLocaleString()}</td><td className="pr-0 text-right">{Number(r.purchases||0).toLocaleString()}</td></tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()
                            )}
                            </>)}
                          </div>
                        </div>
                      ) : (
                        <div className={`${bubbleClass} max-w-[75%] px-3 py-2 rounded-xl whitespace-pre-wrap text-sm leading-5`}>
                          {m.content}
                          {m.retryText && !sending && (
                            <button type="button" onClick={() => sendMessage(m.retryText)} className="ml-2 underline text-violet-700 hover:text-violet-900">Try again</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {sending && !streamHasText && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-2 max-w-[80%]">
                      <div className="h-11 w-11 rounded-full overflow-hidden ring-1 ring-violet-200 flex-shrink-0 mt-0.5 bg-white">
                        <img src="/GlowyAIv3.png" alt="GlowyAI" className="h-full w-full object-cover" />
                      </div>
                      <div className="bg-gray-100 border border-gray-200 px-3 py-2 rounded-2xl text-sm text-gray-600 inline-flex items-center gap-2">
                        <span className="flex gap-0.5">
                          <span className="h-1.5 w-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="h-1.5 w-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="h-1.5 w-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </span>
                        <span>{streamStatus || 'GlowyAI is thinking…'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {showJumpToBottom && (
                <button
                  type="button"
                  onClick={jumpToBottom}
                  className="sticky bottom-3 left-[calc(50%-100px)] w-[200px] z-20 inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-violet-600 text-white text-xs shadow-lg hover:bg-violet-700"
                  aria-label="Jump to latest"
                >
                  <span>↓ Jump to latest</span>
                </button>
              )}
              {(() => {
                const showIntro = (!introDismissed && !activeSessionId && messages.length === 0) || introAnimating;
                if (!showIntro) return null;
                return (
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${introAnimating ? 'opacity-0' : 'opacity-100'}`}>
                    <div className="text-center max-w-lg mx-auto">
                      <div className="relative mx-auto mb-4 h-40 w-40">
                        <div className="absolute inset-0 rounded-full bg-[conic-gradient(#a78bfa_0deg,#f0abfc_120deg,#60a5fa_240deg,#a78bfa_360deg)] animate-[spin_5s_linear_infinite] shadow-[0_0_28px_rgba(167,139,250,.55)]"></div>
                        <div className="absolute inset-[6px] rounded-full bg-white overflow-hidden">
                          <img src="/GlowyAIv3.png" alt="GlowyAI" className="h-full w-full object-cover" />
                        </div>
                      </div>
                      <h2 className="text-base font-semibold text-gray-900">Hi! I’m GlowyAI</h2>
                      <p className="text-sm text-gray-500 mt-1">Ask for highlights, ROAS compares, top channels, or product insights.</p>
                      <div className="mt-4 flex flex-wrap gap-2 justify-center">
                        {suggestions.map((s) => (
                          <button key={s} onClick={() => sendMessage(s)} className="px-3 py-1.5 text-xs rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700">
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="border-t border-gray-300 p-3 flex items-center gap-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your message…"
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending}
                className="px-3 py-2 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 flex items-center justify-center transition-transform hover:scale-[0.98]"
                aria-label="Send"
                title="Send"
              >
                <img src="/send.png" alt="Send" className="h-5 w-5 object-contain" />
              </button>
              {sending && (
                <button
                  type="button"
                  onClick={stopStreaming}
                  className="px-3 py-2 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 text-xs"
                  aria-label="Stop"
                  title="Stop"
                >
                  Stop
                </button>
              )}
            </form>
          </div>
        </div>
        {/* Sidebar (fixed height card with internal scroll) */}
        <aside className="col-span-12 lg:col-span-2 h-full min-h-0">
          <div className="h-full min-h-0 bg-white rounded-2xl shadow-md border border-gray-300 flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-300">
              <h2 className="text-sm font-semibold text-gray-700">Chats</h2>
              <button
                onClick={createNewSession}
                className="p-1 rounded-md hover:bg-gray-100"
                aria-label="New Chat"
                title="New Chat"
              >
                <img src="/post.png" alt="New Chat" className="h-5 w-5 object-contain" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {sessions.length === 0 && (
                <div className="text-xs text-gray-500 text-center py-4">No chats yet</div>
              )}
              {sessions.map((s) => (
                <div
                  key={s.id}
                  onClick={() => selectSession(s.id)}
                  className={`w-full px-2 py-2 rounded-lg border relative cursor-pointer ${activeSessionId===s.id? 'bg-gray-50 border-gray-300':'bg-white border-transparent hover:bg-gray-50'}`}
                >
                  {activeSessionId===s.id && <span className="absolute left-0 top-0 h-full w-1 bg-violet-500 rounded-l-lg" />}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{s.title || 'Untitled'}</div>
                      {s.preview && (
                        <div className="text-xs text-gray-500 truncate mt-0.5">{s.preview}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1 flex items-center justify-between">
                        <span>{s.message_count || 0} messages</span>
                        <span>{s.updated_at ? new Date(s.updated_at).toLocaleDateString('tr-TR', {month: 'short', day: 'numeric'}) : ''}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSession(s.id); }}
                      className={`p-1 rounded-md border ${deletingSessionId===s.id ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-red-50 hover:text-red-600'}`}
                      title="Delete chat"
                      aria-label="Delete chat"
                      disabled={deletingSessionId === s.id}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M6 7h12M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Structured summary UI removed */}

      {/* input moved inside chat card */}
    </div>
  );
}


